import { createSlice, Slice } from '@reduxjs/toolkit';
import get from 'lodash/get';
import reject from 'lodash/reject';
import set from 'lodash/set';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import backToListView from 'src/actions/shared/backToListView';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { DomainError } from 'src/models/DomainError';
import { logout } from 'src/actions/auth/authSlice';
import { registerGateway } from 'src/configuration/registerGateway';
import { registerGatewayObserverCreator } from 'src/configuration/registerGatewayObserverCreator';
import { RemoveOptions, UpdateOptions } from './createResource';
import { toastError, toastSuccess } from 'src/actions/toast';
import validateType, { translateErrors } from 'src/adapters/validateType';

export type DocumentState<T> = {
  value: T;
  loadingState: 'unstarted' | 'loading' | 'succeeded' | 'failed';
  loadingStates: Object;
  persistingState: 'unstarted' | 'persisting' | 'succeeded' | 'failed'; // Maybe needs an associated ID
  errors: Array<DomainError>;
};

export type DocumentEffects = {
  effects: {
    fetch: sharedTypes.AppThunk;
    remove: sharedTypes.AppThunk;
    replace: sharedTypes.AppThunk;
    unload: sharedTypes.AppThunk;
    update: sharedTypes.AppThunk;
    [key: string]: sharedTypes.AppThunk;
  };
};

export type DocumentResource<T> = Slice<DocumentState<T>> & DocumentEffects;

export function handleStandardErrors({ error, dispatch, handleDomainError, handleDefaultError }) {
  if (error instanceof StandardDomainError) {
    dispatch(handleDomainError(translateErrors(error.errors)));
  } else if (error instanceof Unauthorized) {
    dispatch(logout());
  } else {
    dispatch(handleDefaultError());
  }
}

export default function createDocumentResource<T>({
  name,
  endpoint,
  reducers,
  effects,
  observerCreator,
  type,
  initialValue,
  options,
}: {
  name: string;
  endpoint: string;
  reducers?: object;
  effects?: any;
  observerCreator?: any;
  type?: any;
  initialValue?: any;
  options?: {
    additionalRequestParams?: (state: unknown) => Record<string, string>;
    transformDataBeforeStore?: (data: unknown) => unknown;
  };
}): DocumentResource<T> {
  initialValue = (initialValue || {}) as T;
  const initialErrors: DomainError[] = [];

  const initialState: DocumentState<T> = {
    value: initialValue,
    loadingState: 'unstarted',
    loadingStates: {},
    persistingState: 'unstarted',
    errors: initialErrors,
  };

  // This redux-starter helper function creates the actions for you
  // actions only affect the redux state
  const slice = createSlice({
    name: name,
    initialState,
    reducers: {
      deleteStarted(state) {
        state.persistingState = 'persisting';
      },
      deleteSuccess(state) {
        state.persistingState = 'succeeded';
      },
      // TODO: add BE error handler
      // eslint-disable-next-line
      deleteRequestError(state, action) {
        state.persistingState = 'failed';
      },
      deleteError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },
      fetchStarted(state) {
        state.loadingState = 'loading';
        state.errors = [];
      },
      fetchSuccess(state?, action?) {
        state.loadingState = 'succeeded';
        state.value = action.payload;
      },
      fetchRequestError(state?) {
        state.loadingState = 'failed';
      },
      fetchError(state?, action?) {
        state.loadingState = 'failed';
        state.errors = action.payload;
      },
      updateStarted(state?) {
        state.persistingState = 'persisting';
      },
      updateOrderItems(state, { payload }) {
        const { pathToValue, currentItemId, replaceItemId } = payload;
        const key = pathToValue ? `value.${pathToValue}` : `value`;
        const list = get(state, key, []);

        // change order
        const currentIndex = list.findIndex((item: any) => item.id === currentItemId);
        const endIndex = list.findIndex((item: any) => item.id === replaceItemId);
        const [removed] = list.splice(currentIndex, 1);
        list.splice(endIndex, 0, removed);

        set(state, key, list);
      },
      updateSuccess(state?) {
        state.persistingState = 'succeeded';
      },
      updateRequestError(state?) {
        state.persistingState = 'failed';
      },
      updateError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },

      validationError(state, action: { payload: DomainError[] }) {
        state.errors = action.payload;
      },

      broadcast(state, action) {
        const item = action.payload;
        state.value = item;
      },

      broadcastDelete(state) {
        state.value = initialValue;
      },

      resetErrors(state, action) {
        let errs: any[] = [];

        // If an action is defined, then reset the error specified in the action - otherwise reset all errors
        if (action.payload) {
          const attr = action.payload;
          errs = reject(state.errors, { attr });
        }

        state.persistingState = 'unstarted';
        state.errors = errs;
      },

      reset() {
        return initialState;
      },

      ...reducers,
    },
  });

  const { broadcast, reset } = slice.actions;

  const validate: sharedTypes.AppThunk = (attrs: any, overrideType?: any) => dispatch => {
    const { validationError } = slice.actions;
    const validationType = overrideType || type;
    if (!validationType) {
      return;
    }
    try {
      validateType(validationType, attrs);
    } catch (err) {
      dispatch(validationError(translateErrors((err as any).errors)));
      return false;
    }
    return true;
  };

  const fetch: sharedTypes.AppThunk =
    (params: any) =>
    async (dispatch, getState, { gateways }) => {
      const { fetchStarted, fetchSuccess, fetchError, fetchRequestError } = slice.actions;
      if (options?.additionalRequestParams) {
        const additionalParams = options?.additionalRequestParams(getState());
        params = { ...params, ...additionalParams };
      }

      try {
        dispatch(fetchStarted());
        let result = await gateways[name].request(params);

        if (options?.transformDataBeforeStore) {
          result = options.transformDataBeforeStore(result);
        }

        dispatch(fetchSuccess(result));
      } catch (error) {
        handleStandardErrors({ error, dispatch, handleDomainError: fetchError, handleDefaultError: fetchRequestError });
      }
    };

  /**
   * Uses standard gateway update method (which uses HTTP PATCH)
   * @param params
   * @param attrs
   */

  const update: sharedTypes.AppThunk =
    (params: any, attrs: any, opts?: UpdateOptions) =>
    async (dispatch, getState, { gateways }) => {
      const { updateStarted, updateSuccess, updateError, updateRequestError } = slice.actions;

      try {
        dispatch(updateStarted());
        await gateways[name].update(params, attrs);
        dispatch(updateSuccess());

        dispatch(toastSuccess(opts?.customToastMsg || 'Update successful!'));
      } catch (error) {
        handleStandardErrors({
          error,
          dispatch,
          handleDomainError: updateError,
          handleDefaultError: updateRequestError,
        });
        dispatch(toastError(`There was an error while updating`));
      }
    };

  /**
   * Uses standard gateway update method (which uses HTTP DELETE)
   * @param params
   * @param opts
   */

  const remove: sharedTypes.AppThunk =
    (params: any, opts?: RemoveOptions) =>
    async (dispatch, getState, { gateways }) => {
      const { deleteStarted, deleteSuccess, deleteError, deleteRequestError } = slice.actions;
      try {
        dispatch(deleteStarted());
        await gateways[name].delete(params);
        dispatch(deleteSuccess());
        dispatch(toastSuccess((opts && opts.customToastMsg) || 'Delete successful!'));
      } catch (err) {
        if (err instanceof Unauthorized) {
          dispatch(logout());
        } else {
          dispatch(deleteRequestError(err));
        }
        handleStandardErrors({
          error: err,
          dispatch,
          handleDomainError: deleteError,
          handleDefaultError: deleteRequestError,
        });
        dispatch(toastError('There was an error while deleting'));
      }
    };

  /**
   * Uses standard gateway replace method (which uses HTTP PUT)
   * @param params
   * @param attrs
   * @param options
   */

  const replace: sharedTypes.AppThunk =
    (
      params: any,
      attrs: any,
      options: {
        navigateBack: boolean;
        successToastMessage?: string;
        errorToastMessage?: string;
        doNotShowToast?: boolean;
      } = {
        navigateBack: false,
        successToastMessage: '',
        errorToastMessage: '',
      }
    ) =>
    async (dispatch, getState, { gateways }) => {
      const { updateStarted, updateSuccess, updateError, updateRequestError } = slice.actions;

      try {
        dispatch(updateStarted());
        await gateways[name].replace(params, attrs);
        dispatch(updateSuccess());
        const successToastMessage = options.successToastMessage || 'Update successful!';

        if (!options.doNotShowToast) {
          dispatch(toastSuccess(successToastMessage));
        }

        if (options.navigateBack) {
          backToListView();
        }

        return true;
      } catch (error) {
        handleStandardErrors({
          error,
          dispatch,
          handleDomainError: updateError,
          handleDefaultError: updateRequestError,
        });

        if (!options.doNotShowToast) {
          dispatch(toastError(options?.errorToastMessage || 'There was an error while updating'));
        }

        return false;
      }
    };

  const unload: sharedTypes.AppThunk =
    (params: any) =>
    async (dispatch, getState, { gateways }) => {
      await gateways[name].unsubscribe(params);
      dispatch(reset());
    };

  const defaultCreateObserver = ({ dispatch }) => ({
    onBroadcast(item) {
      dispatch(broadcast(item));
    },
  });
  const createObserver = observerCreator ? observerCreator({ actions: slice.actions }) : defaultCreateObserver;

  registerGatewayObserverCreator(name, createObserver);
  registerGateway(name, endpoint);
  return {
    ...slice,
    // Effects cause API requests (mostly). They can (and do) call actions to also change redux store
    effects: {
      fetch,
      remove,
      replace,
      unload,
      update,
      validate,
      ...effects,
    },
  };
}
